/* @flow */

import React from 'react';
import PropTypes from 'prop-types';
import AudioMutedIndicator from './AudioMutedIndicator';
import AudioUnmutedIndicator from './AudioUnmutedIndicator';
import SpeakerOffIndicator from './SpeakerOffIndicator';
import SpeakerOnIndicator from './SpeakerOnIndicator';
import VideoMutedIndicator from './VideoMutedIndicator';
import VideoUnmutedIndicator from './VideoUnmutedIndicator';
import withBoundary from 'component/ErrorBoundary/withBoundary';
import { connect } from 'store/modules/base/redux/functions';
import {
  isLocalTrackMuted,
  isRemoteTrackMuted
} from 'store/modules/base/tracks/functions';
import { MEDIA_TYPE } from 'store/modules/base/media/constants';
import {
  getParticipantByIdOrUndefined,
  isScreenShareParticipantById
} from 'store/modules/base/participants/functions';
import { getIndicatorsTooltipPosition } from 'store/modules/filmstrip/functions';

/**
 * React {@code Component} for showing the status bar in a thumbnail.
 *
 * @augments Component
 */
class StatusIndicators extends React.PureComponent {
  /**
   * Implements React's {@link Component#render()}.
   *
   * @inheritdoc
   * @returns {ReactElement}
   */

  render() {
    const styles = {
      flexDirection: 'row',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginTop: '2px'
    };
    const {
      _showAudioMutedIndicator,
      _showVideoMutedIndicator,
      _showSpeakerOnIndicator,
      thumbnailType
    } = this.props;
    const tooltipPosition = getIndicatorsTooltipPosition(thumbnailType);

    return (
      <div style={styles}>
        {_showAudioMutedIndicator && (
          <AudioMutedIndicator tooltipPosition={tooltipPosition} />
        )}
        {!_showAudioMutedIndicator && (
          <AudioUnmutedIndicator tooltipPosition={tooltipPosition} />
        )}
        {_showSpeakerOnIndicator ? (
          <SpeakerOnIndicator tooltipPosition={tooltipPosition} />
        ) : (
          <SpeakerOffIndicator tooltipPosition={tooltipPosition} />
        )}
        {_showVideoMutedIndicator && (
          <VideoMutedIndicator tooltipPosition={tooltipPosition} />
        )}
        {_showVideoMutedIndicator && (
          <VideoUnmutedIndicator tooltipPosition={tooltipPosition} />
        )}
      </div>
    );
  }
}

/**
 * Maps (parts of) the Redux state to the associated {@code StatusIndicators}'s props.
 *
 * @param {Object} state - The Redux state.
 * @param {Object} ownProps - The own props of the component.
 * @private
 * @returns {{
 *     _showAudioMutedIndicator: boolean,
 *     _showModeratorIndicator: boolean,
 *     _showScreenShareIndicator: boolean
 * }}
 */
function _mapStateToProps(state, ownProps) {
  const { participantID, audio } = ownProps;

  // Only the local participant won't have id for the time when the conference is not yet joined.
  const participant = getParticipantByIdOrUndefined(state, participantID);
  const tracks = state['features/base/tracks'];

  let isAudioMuted = true;
  let isVideoMuted = true;

  if (participant?.local) {
    isAudioMuted = isLocalTrackMuted(tracks, MEDIA_TYPE.AUDIO);
    isVideoMuted = isLocalTrackMuted(tracks, MEDIA_TYPE.VIDEO);
  } else if (
    !participant?.fakeParticipant ||
    isScreenShareParticipantById(state, participantID)
  ) {
    isAudioMuted = isRemoteTrackMuted(tracks, MEDIA_TYPE.AUDIO, participantID);
    isVideoMuted = isRemoteTrackMuted(tracks, MEDIA_TYPE.VIDEO, participantID);
  }

  return {
    _showAudioMutedIndicator: isAudioMuted && audio,
    _showVideoMutedIndicator: isVideoMuted && audio,
    _showSpeakerOnIndicator: participant?.customFakeSpeaker === 'on'
  };
}

export default connect(_mapStateToProps)(withBoundary(StatusIndicators));

/**
 * The type of the React {@code Component} props of {@link StatusIndicators}.
 */
StatusIndicators.propTypes = {
  /**
   * Indicates if the audio muted indicator should be visible or not.
   */
  _showAudioMutedIndicator: PropTypes.bool,
  _showVideoMutedIndicator: PropTypes.bool,
  _showSpeakerOnIndicator: PropTypes.bool,

  /**
   * Indicates if the moderator indicator should be visible or not.
   */
  _showModeratorIndicator: PropTypes.bool,

  /**
   * Indicates if the screen share indicator should be visible or not.
   */
  _showScreenShareIndicator: PropTypes.bool,

  /**
   * The ID of the participant for which the status bar is rendered.
   */
  participantID: PropTypes.string,

  /**
   * The type of thumbnail.
   */
  thumbnailType: PropTypes.string
};
